import React from 'react';
import { Dialog, Button, Text, Stack, Icon, Box, Portal } from '@chakra-ui/react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';
import { useTranslate } from '@tolgee/react';

interface ReloadConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  showTimestamp?: boolean;
  timestamp?: number;
}

const ReloadConfirmationDialog: React.FC<ReloadConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
}) => {
  const { t } = useTranslate();
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            mx={4}
            maxW="md"
            bg="white"
            borderRadius="xl"
            boxShadow="2xl"
            border="1px solid"
            borderColor="gray.200"
          >
            <Dialog.Header
              display="flex"
              alignItems="center"
              gap={3}
              pb={4}
              borderBottom="1px solid"
              borderColor="gray.200"
            >
              <Box p={2} borderRadius="full" bg="orange.100" color="orange.600">
                <Icon as={FiAlertTriangle} boxSize={5} />
              </Box>
              <Stack direction="column" gap={0} align="start">
                <Text fontSize="lg" fontWeight="bold" color="gray.800">
                  {title || t('reload-confirmation-dialog.title', 'Xác nhận rời khỏi trang')}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {t('reload-confirmation-dialog.subtitle', 'Cảnh báo thay đổi chưa lưu')}
                </Text>
              </Stack>
            </Dialog.Header>
            <Dialog.Body py={6}>
              <Stack gap={4} align="start">
                <Stack direction="row" gap={3} align="start">
                  <Icon as={FiInfo} color="blue.500" boxSize={4} mt={0.5} />
                  <Text color="gray.700" fontSize="sm">
                    {message ||
                      t(
                        'reload-confirmation-dialog.message',
                        'Bạn có chắc chắn muốn rời khỏi trang? Những thay đổi chưa được lưu sẽ bị mất.'
                      )}
                  </Text>
                </Stack>

                <Box p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200" w="full">
                  <Text fontSize="xs" color="blue.700">
                    💡 <strong>{t('reload-confirmation-dialog.tip-label', 'Mẹo:')}</strong>{' '}
                    {t(
                      'reload-confirmation-dialog.tip-message',
                      'Bạn có thể lưu thông tin trước khi rời khỏi trang để tránh mất dữ liệu.'
                    )}
                  </Text>
                </Box>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer pt={4} borderTop="1px solid" borderColor="gray.200">
              <Stack direction="row" gap={3} w="full">
                <Button
                  onClick={onClose}
                  variant="outline"
                  bgColor="gray.100"
                  fontWeight="semibold"
                  border="1px solid"
                  borderColor="gray.200"
                  flex={1}
                  size="md"
                  borderRadius="lg"
                >
                  {cancelText || t('reload-confirmation-dialog.cancel', 'Stay')}
                </Button>
                <Button
                  onClick={onConfirm}
                  bgColor="red.600"
                  color="white"
                  fontWeight="semibold"
                  border="1px solid"
                  borderColor="red.600"
                  flex={1}
                  size="md"
                  borderRadius="lg"
                  _hover={{
                    bg: 'red.600',
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg',
                  }}
                  transition="all 0.2s"
                >
                  {confirmText || t('reload-confirmation-dialog.confirm', 'Leave')}
                </Button>
              </Stack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ReloadConfirmationDialog;
